import moment from 'moment';
import _ from 'lodash';
import { isValidElement, useEffect, useMemo, useRef, useState } from 'react';
import { KEY_RECOIL, TYPE_RHYTHM_EVENT_ENUM } from '@/shared/config';
import { storeTimeout } from '@/shared/lib/storeTimeout';
import {
  calculateStartStopStripMomentOfListEventV2,
  getEventEffectdByUpdatedEvent,
} from '@/entities/event';
import { checkAndProcessBeatData, generateUpdatedBeatsV2 } from '@/entities/beat';
import { checkAndProcessEcgData } from '@/entities/ecg';
import { logError } from '@/shared/lib/apiHelpers';
import {
  selectActiveTab,
  selectBeatChanges,
  selectBulkBeatsChanges,
  selectEventChanges,
  selectEventNews,
  selectResetBulkBeatsChanges,
  useReportStore,
} from '@/entities/report';
import type { EventPreviewStripProps } from './eventPreviewStrip';

/**
 * Loads and keeps current the ECG waveform, beat markers and beat statuses drawn in one event preview strip on the beats tab. Fetches the strip's ECG and beat data on mount and whenever the event or the beat changes shift, re-applies pending and bulk beat changes to the markers, staggers a follow-up cache prefetch by strip index, resolves the pending change for the event, and clears its timers when the active tab changes.
 *
 * @param props - Preview strip props: the event, its index, the ECG data map, custom offsets, channels, overlay content and the selection callback.
 * @returns An object with the overlay content, ECG samples, beat markers and HES beat statuses, the pending change for the event, the click handler, whether the event was edited, whether the tab hides heart rate, and the resolved channel list.
 */
const useEventPreviewStrip = ({
  overlayContent: overlayContentProp,
  event,
  ecgDataMap,
  customOffset,
  index,
  channels: channelsProp,
  onClickSelectEvent,
}: Pick<
  EventPreviewStripProps,
  | 'overlayContent'
  | 'event'
  | 'ecgDataMap'
  | 'customOffset'
  | 'index'
  | 'channels'
  | 'onClickSelectEvent'
>) => {
  const overlayContent = overlayContentProp ?? null;
  const keyRecoil = KEY_RECOIL.BEAT_HR;
  const activeButton = useReportStore(selectActiveTab(keyRecoil));
  const eventChanges = useReportStore(selectEventChanges(keyRecoil));
  const eventNews = useReportStore(selectEventNews(keyRecoil));
  const beatChanges = useReportStore(selectBeatChanges(keyRecoil));

  const [ecgData, setEcgData] = useState([]);
  const [beatMarkers, setBeatMarkers] = useState([]);
  const [hesBeatStatus, setHesBeatStatus] = useState([]);

  const bulkBeatsChanges = useReportStore(selectBulkBeatsChanges(keyRecoil));
  const resetBulkBeatsChanges = useReportStore(selectResetBulkBeatsChanges(keyRecoil));

  const timeoutRef = useRef<any>();

  const eleChange = useMemo(() => {
    const { event: effectedEvent } = getEventEffectdByUpdatedEvent(event, eventNews, eventChanges);
    return eventChanges.find((x: any) => x.idEvent === event.idEvent) || effectedEvent;
  }, [eventChanges, event, eventNews]);
  let isActiveQuery: boolean;

  /**
   * Schedules a background prefetch of the ECG samples surrounding this event's preview strip. After the delay it deregisters its own timer from the per-tab timeout store, derives the default strip start and stop moments from the event start epoch, and requests that window sized by the sampling frequency, keeping the timer id in a ref and in the store so a tab switch can cancel it.
   *
   * @param waitTime - Delay in milliseconds before the prefetch runs; defaults to 0.
   * @returns Nothing.
   */
  const downloadCacheECGDataV2 = (waitTime = 0) => {
    const timerId = setTimeout(() => {
      storeTimeout.removeStore(activeButton as any, timerId);
      const { defaultStartStrip, defaultStopStrip } = calculateStartStopStripMomentOfListEventV2({
        startTimeEpoch: event.startTimeEpoch,
      });
      checkAndProcessEcgData({
        ecgDataMap,
        dataLength:
          ((defaultStopStrip.valueOf() - defaultStartStrip.valueOf()) / 1000) *
          ecgDataMap.samplingFrequency,
        momentObject: {
          startMoment: defaultStartStrip,
          stopMoment: defaultStopStrip,
        } as any,
      });
    }, waitTime);
    timeoutRef.current = timerId;
    storeTimeout.pushStore(activeButton as any, timerId);
  };

  /**
   * Fetches the five-second ECG window and beat data for this preview strip and pushes them into local state. Awaits the ECG and beat processing for the event with its custom offsets and, only while the effect is still active, stores the samples, rebuilds beat markers and HES statuses with pending and bulk beat changes applied, then queues the staggered prefetch; cancellation errors are swallowed and the rest logged.
   *
   * @returns A promise resolving once the strip's ECG and beat state has been populated.
   */
  const downloadEventStrip = async () => {
    try {
      const dataLength = ecgDataMap.samplingFrequency * 5;
      let ecgData: any = await checkAndProcessEcgData({
        ecgDataMap,
        beat: event,
        dataLength,
        config: { customOffset } as any,
      });
      // Check and process beat data
      let beatData: any = await checkAndProcessBeatData({
        ecgDataMap,
        beat: event,
        config: { customOffset } as any,
      });

      if (isActiveQuery) {
        setEcgData(ecgData);
        if (beatData) {
          const { updatedBeats, updatedHesBeatStatus } = generateUpdatedBeatsV2({
            beats: beatData?.beats,
            hesBeatStatus: beatData?.hesBeatStatus,
            beatChanges,
            startEpoch: moment(event.id - customOffset[0]).valueOf(),
            stopEpoch: moment(event.id + customOffset[1]).valueOf(),
            beatHeatMapIds: beatData?.beatHeatMapIds,
            applyBulkBeatsChanges: bulkBeatsChanges,
            resetAppliedBulkBeatsChanges: resetBulkBeatsChanges,
          } as any);
          setBeatMarkers(updatedBeats);
          setHesBeatStatus(updatedHesBeatStatus);
        }
        downloadCacheECGDataV2(index * 300);
      }

      ecgData = null;
      beatData = null;
    } catch (error) {
      if (!_.includes(error.message, 'canceled')) {
        logError(error.message, 'Error');
      }
    }
  };

  /**
   * Selects the event behind this preview strip on a plain click. Swallows the click when an overlay element is rendered over the strip or when Shift, Ctrl or Meta is held, since those belong to multi-select, otherwise forwards the event and its index to the parent selection callback.
   *
   * @param e - Mouse event carrying the modifier key flags.
   * @returns Nothing.
   */
  const onClick = (e: any) => {
    // Prevent click to select new event,shift or ctrl when select multiple
    if (isValidElement(overlayContent) || e?.shiftKey || e?.ctrlKey || e?.metaKey) {
      e?.stopPropagation();
      e?.preventDefault();
      return;
    }
    onClickSelectEvent(event, index);
  };

  useEffect(() => {
    isActiveQuery = true;
    downloadEventStrip();
    return () => {
      isActiveQuery = false;
    };
  }, [event.id, beatChanges, bulkBeatsChanges, resetBulkBeatsChanges]);

  useEffect(
    () => () => {
      storeTimeout.removeStore(activeButton as any, timeoutRef.current);
    },
    [activeButton],
  );

  const isEdited = !!eleChange;
  const isNoHrEvent = TYPE_RHYTHM_EVENT_ENUM.ARTIFACT === activeButton;
  const channels = channelsProp?.includes(4) ? [event.channel] : channelsProp;

  return {
    overlayContent,
    ecgData,
    beatMarkers,
    hesBeatStatus,
    eleChange,
    onClick,
    isEdited,
    isNoHrEvent,
    channels,
  };
};

export default useEventPreviewStrip;
